/**
 * Protocol types, opcodes, constants. Hand-written, no schema library at
 * runtime. All encode/decode lives in this one file so a future migration to
 * binary is a file change, not a codebase sweep.
 *
 * Discriminated union on a numeric opcode. TypeScript exhaustiveness on the
 * union (switch with a `never` default) catches missing cases at compile time.
 *
 * Two opcode families:
 * - Gameplay ops (Move..GenerateGatestone) carry `seq` — the client's
 *   monotonic sequence number; the server stamps the tick on receipt.
 * - Pre-game ops (Hello, Start) are connection/room lifecycle, not sim
 *   commands; they have no seq and never reach the simulation.
 */

import type { HeroAppearance } from "./appearance.js";
import type { PocketItem } from "./observation.js";
import { EQUIP_SLOTS } from "./items.js";
import type { EquipSlot, ItemDef } from "./items.js";
import { isHeroClass } from "./classes.js";
import type { ClassCatalog, HeroClass } from "./classes.js";
import type { CombatTables } from "./combat.js";

/**
 * The fixed id of the persistent hub (the town — docs/design/hub.md).
 * Deliberately NOT a Room: no sim, no seed, no fog, no hash stream, and it
 * never closes. A Hello with no roomId (or roomId = this) lands the client
 * in the hub; a Welcome carrying this roomId tells the client to render the
 * hub scene, not a floor. The hub is the lobby: no room codes, no create
 * flow — a floor is created at the door (server seed) and the player's
 * socket is re-parented into it.
 */
export const HUB_ROOM_ID = "hub";

/**
 * The town's plane, in tiles. Shared because the server has to bound what it
 * relays: a client that may claim any coordinate can claim one outside the
 * plane the other clients draw — a hero nobody can find. The client draws
 * exactly this grid, so layout and validation cannot drift apart.
 *
 * It is a PRESENTATION space, not a sim one: nothing in here is generated,
 * revealed, or hashed, and no run can read it (AGENTS §2's hub boundary).
 */
export const HUB_PLANE_W = 40;
export const HUB_PLANE_H = 40;

export enum ClientOp {
  Move = 1,
  Interact = 2,
  UseAbility = 3,
  PlaceGatestone = 4,
  TeleportGatestone = 5,
  Chat = 6,
  GenerateGatestone = 7,
  Hello = 8,
  Start = 9,
  /** M4 HUD: drag-rearrange your inventory (both held-key and carried-stone
   *  order — the map is a permanent first entry and is never in either
   *  array). The client sends the desired order; the sim validates it is a
   *  permutation of the current lists before applying — a client can never
   *  add/remove/hide items, only reorder what it already holds. */
  ReorderInventory = 10,
  /** Drop a held key back onto the floor at your feet. Keys are interact-
   *  to-pickup; dropping is the inverse — the key entity leaves your
   *  inventory and reappears on the tile you stand on. */
  DropKey = 11,
  /** Equip an item from your inventory into its slot. If the slot is
   *  occupied, the occupant is swapped into the equipped item's inventory
   *  position (the grid slot the new item takes). itemId is a catalog kind
   *  index (shared/items.ts); unknown kinds are rejected by the sim. */
  EquipItem = 12,
  /** Unequip a slot: the item goes to the END of your inventory. Rejected
   *  with "Your inventory is full" when the inventory cap is reached, and
   *  with a no-op-style invalid when the slot is empty. */
  UnequipItem = 13,
  /**
   * Melee attack — a STANDING ORDER, not a one-shot swing (RuneScape-style
   * auto-attack). Sets the attacker's order to chase + auto-attack an enemy
   * (targetId, targetIsEnemy): the sim routes the attacker into range and
   * swings on its weapon's cadence until the target dies or the order is
   * replaced (a Move command walks away and clears it; a new Attack
   * re-targets). Re-issuing the same order is an idempotent refresh. Rejected
   * with an invalid event only when the target is missing/dead — there is no
   * range check on receipt: approaching IS the order (sim/combat.ts).
   */
  Attack = 14,
  /**
   * Report where MY hero stands in the town (docs/design/hub.md). PRE-GAME:
   * no seq — it never reaches the simulation, and the hub has no command
   * order for it to join. Valid only for a socket that lives in the hub; a
   * floor socket's report is ignored.
   *
   * PRESENCE, not authority. The sender moves only itself; the server clamps
   * the tile into the plane and relays it — no collision, no fog, no pathing,
   * no state any run can see. The town exists to see other players, and a
   * roster that cannot move is a list, not a place: without this the crowd is
   * frozen on their spawn slots and walking looks like nothing.
   */
  HubMove = 15,
  /**
   * Open the dungeon door (docs/design/hub.md): the whole start flow.
   * PRE-GAME: no seq — it is a space transition, not a sim command. Valid
   * only for a socket that lives in the hub; a floor socket's knock is
   * refused. There is NO seed field on purpose (the seed rule, hub.md):
   * a client may never choose or receive the seed of a floor it is about
   * to run — the server picks it at the instant the door opens. `floor`
   * is the floor recipe id (floors.store — FLOORS) and `size` the dungeon
   * size; the server validates the id and closes on an unknown one.
   */
  OpenDoor = 16,
  /**
   * The party verbs (docs/design/hub.md — the hub's party structure).
   * PRE-GAME: no seq — the hub has no sim, so there is no command stream
   * for them to enter. Valid only for a socket that lives in the hub; a
   * floor socket's party op is refused. `target`/`from` are HUB member ids
   * (the roster's id space — the only ids the town's wire uses). The party
   * itself is keyed server-side by the ACCOUNT record id, which is never on
   * the wire (it is the stable handle across the hub↔floor round trip).
   */
  PartyInvite = 17,
  /** Accept a pending group-up invite: `from` = the inviter's hub id. */
  PartyAccept = 18,
  /** Decline a pending group-up invite: `from` = the inviter's hub id. */
  PartyDecline = 19,
  /** Leave your own party (a reason error when you are not in one). */
  PartyLeave = 20,
  /**
   * Use a CONSUMABLE from your pocket (design/loot.md): `slot` is the
   * pocket-grid index into the (class, kind) item list — the sim resolves
   * the entry, rejects equipment (the pocket is class-tagged), and applies
   * the effect: heal = instant +HP capped at max, buff = a timed multiplier
   * (magnitude 100 = 1×) that REFRESHES its type (newer use sets magnitude
   * and duration; same type never stacks). Any time, in combat or not —
   * no cast, no cooldown: the consumable IS the cooldown (it is consumed).
   */
  UseItem = 21,
  /**
   * Say something in the town (docs/design/hub.md — "Chat, server-
   * broadcast, no sim"). PRE-GAME: no seq — the hub has no sim, so there is
   * no command stream for it to enter. Valid only for a socket that lives in
   * the hub; a floor socket's message is refused (the floor's Chat=6 is the
   * sim's and rides a tick; this is the hub's, and the two never mix).
   * The message is a plain string: empty or beyond the wire bound is
   * malformed, and the server truncates longer-in-practice text to the same
   * chat length the floor uses (combat.store's `n`) — one visible limit for
   * both chat surfaces.
   */
  HubChat = 22,
  /**
   * Ask the Bull to replace the account's next-run class/gear. Available to
   * every signed-in account. This is a pre-game, hub-only packet — it has no
   * seq and never reaches the simulation. The opcode keeps its legacy name.
   */
  PlaytestLoadout = 23,
  /**
   * Drop a POCKET item (equipment or consumable — design/loot.md: items
   * were pocket-only until this op) back onto the floor at your feet. The
   * item leaves the pocket and reappears as a ground drop (Interact "item"
   * re-picks it up — the same pile the loot rolls make). `slot` is the
   * pocket-grid index, like UseItem (the sim resolves the entry). Drops are
   * never blocked by the inventory cap (a drop is the way OUT of a full
   * pocket — the no-softlock rule keys already set).
   */
  DropItem = 24,
  /**
   * Transfer the HUB PARTY's leadership (docs/design/hub.md, amended
   * 2026-10-25): `target` is the hub member id of the new host — must be a
   * MEMBER of the sender's party, and the sender must be the CURRENT host
   * (the server is the judge; the client gates the verb for courtesy).
   * PRE-GAME: no seq — the same discipline as the other party verbs.
   * When a host LEAVES or DROPS instead, the host re-derives to the lowest
   * account id still in (run.md's transfer rule, now the fallback).
   */
  PartyTransfer = 25,
  /** Leave the floor from anywhere, using the return portal's exit rules. */
  LeaveFloor = 26,
  /** Transport-only: request a current party-filtered snapshot. */
  Sync = 27,
  /** Transport-only: acknowledge an observation after the UI applied it. */
  Ack = 28,
  /**
   * FOUND A PARTY (docs/design/keyracing.md §Party model, chunk A1): you are
   * not in a party until you create one or join one someone created. This is
   * the only way a party comes into existence — the old implicit founding
   * (a solo inviter's first accept created one) is retired, because keyracing
   * must be statable about a solo: **a party of one is a real party**, and it
   * is what holds a solo race. PRE-GAME: no seq, hub-only, like the other
   * party verbs. Refused when you already hold a party (a second create is a
   * reason error, never a replacement).
   */
  PartyCreate = 29,
  /**
   * CHALLENGE a party to a keyrace (docs/design/keyracing.md §The shape of a
   * race, chunk C1): two parties run TWO instances of ONE server-drawn seed and
   * the clear times are compared. PRE-GAME: no seq, hub-only, like the party
   * verbs. `target` is a HUB member id — any member of the party being
   * challenged, which is how the town's UI names them; the challenge goes to
   * that party, not to that player.
   *
   * The ISSUER must be the HOST of their own party (the server is the judge),
   * and the issuer's own acceptance is implicit — they chose the terms. The
   * terms are LOCKED here: there are no counter-offers, so a different offer is
   * a new challenge. `floor`/`size` are validated exactly like OpenDoor, and
   * `size` is restricted to medium|large: **small is not raceable**.
   *
   * Nothing here is a seed. The seed is drawn by the server at ARM (the last
   * acceptance) and never leaves the server (§6 holds unchanged).
   */
  RaceChallenge = 30,
  /**
   * Accept an open challenge (docs/design/keyracing.md §Party model). `race` is
   * the wire race id from `ServerOp.RaceState`. EVERY member of BOTH parties
   * must accept — it is their Elo on the line — and the race ARMS the instant
   * the last one does. Accepting is only legal for a named member who has not
   * already accepted; anyone else gets a reason.
   *
   * There is no countdown to buy down and no timer that can carry you in: an
   * unanswered challenge expires, and nothing arms by a clock running out.
   */
  RaceAccept = 31,
  /**
   * REJECT an open challenge (host of either side ONLY — the host is the one an
   * incoming challenge is put in front of). Kills it for both sides: no floor,
   * no rows, no rating. A non-host has no decline verb; their opt-out is
   * `PartyLeave`, which costs them the party (keyracing.md §Party model).
   */
  RaceReject = 32,
  /**
   * OPEN THE RACE DOOR (docs/design/keyracing.md C2) — host of one side only.
   * Takes that side's party into its own floor, generated from the race's
   * locked seed, with the race's floor type and size. There is no packet
   * naming the seed and none naming the terms: the race id selects both, so a
   * client cannot shop for a floor or a map by asking nicely.
   *
   * Each side opens its OWN door from its OWN host, at its own moment; the two
   * rooms share a seed and nothing else. The dialog's "Cancel" sends NOTHING —
   * that is the whole of its semantics, and it is why an armed race survives a
   * declined prompt (the 30-minute start window, C3, is what a stall runs into).
   */
  RaceBegin = 33,
  /**
   * ASK FOR A PLAYER CARD (docs/design/keyracing.md D1b) — pre-game, hub-only,
   * no seq, like every party and race verb: the town has no sim for it to enter.
   *
   * A PULL, not a pushed field. The client cannot name an account at all (wire
   * member ids are HUB ids), so the server resolves the lookup either way — a
   * pushed rating would only precompute it for every presence on every
   * `HubState`, for a number that changes once per race and is read by nobody
   * until a menu opens. Hub-only is also the structural guarantee that rating
   * never reaches a floor wire, exactly as hub `x`/`y` cannot (§6).
   *
   * The answer is `ServerOp.PlayerCard`, to this socket alone. An unknown or
   * stale id gets no reply at all: a menu that has already closed is not a
   * question that needs answering, and an error line would be noise.
   */
  InspectPlayer = 34,
}

/**
 * A floor room's capacity — and the party's cap. ONE number, on purpose
 * (hub.md's build notes): the cap is enforced at INVITE time, so a party
 * can never outgrow the room the door gives it, and the door can never
 * fail on capacity. The door opens a room of exactly this size.
 */
export const FLOOR_CAPACITY = 5;

export type InteractKind = "door" | "boss" | "key" | "stone" | "portal" | "item" | "sack" | "puzzle-imbue" | "puzzle-force" | "inspect-chasm" | "wizard-orb";

/**
 * A floor item a client can name as a cast target (the Squire's Telegrab):
 * a key or a gatestone. Keys and stones have overlapping id spaces, so the
 * kind is part of the reference (like Attack's targetIsEnemy).
 */
export type ItemKind = "key" | "stone";

export type ClientPacket =
  | { op: ClientOp.Move; seq: number; x: number; y: number }
  | { op: ClientOp.Interact; seq: number; kind: InteractKind; targetId: number }
  /**
   * Use a class ability (abilityId 0–2). `targetId` is -1 for abilities that
   * take no target (most of them). `x`/`y` (both or neither) is the AIMED
   * TILE for the aimed kinds (pull-vortex — Black Hole casts on a tile;
   * teleport — Blink; telegrab — the click target). `targetId` +
   * `targetKind` (both or neither) name a FLOOR ITEM for telegrab; the
   * other kinds ignore them.
   */
  | {
      op: ClientOp.UseAbility;
      seq: number;
      abilityId: number;
      targetId: number;
      x?: number;
      y?: number;
      targetKind?: ItemKind;
    }
  | { op: ClientOp.PlaceGatestone; seq: number; stoneId: number }
  | { op: ClientOp.TeleportGatestone; seq: number; which: "personal" | "group" }
  | { op: ClientOp.Chat; seq: number; message: string }
  | { op: ClientOp.GenerateGatestone; seq: number }
  | { op: ClientOp.ReorderInventory; seq: number; keys: number[]; carried: number[]; items: PocketItem[] }
  | { op: ClientOp.DropKey; seq: number; keyId: number }
  | { op: ClientOp.EquipItem; seq: number; itemId: number }
  | { op: ClientOp.UnequipItem; seq: number; slot: string }
  | { op: ClientOp.Attack; seq: number; targetId: number; targetIsEnemy: boolean }
  | { op: ClientOp.UseItem; seq: number; slot: number }
  | { op: ClientOp.LeaveFloor; seq: number }
  | {
      op: ClientOp.Hello;
      name: string;
      roomId?: string;
      token?: string;
      size?: string;
      capacity?: number;
      seed?: number;
      /**
       * The floor recipe's id (floors.store's projection — shared/floors.ts
       * FLOORS): the room's spawn pools (which NPCs may spawn in rooms, which
       * may be the boss). Create-only (joiners inherit the room's recipe). The
       * server validates it against FLOORS — an unknown id closes the
       * connection. Absent = the legacy all-NPC run (the headless paths —
       * play, agents — and old clients keep working).
       */
      floor?: number;
      /**
       * Account session token (POST /login). A server with accounts
       * configured resolves it to the account and binds the identity: the
       * account's username OVERRIDES `name` (a client never impersonates
       * another account by renaming), and the saved appearance travels with
       * the player. An unresolvable token closes the connection ("auth
       * expired"). A server without accounts ignores it (arena, lab,
       * headless).
       */
      auth?: string;
      /** Opt into bounded, acknowledged snapshot delivery. */
      recovery?: boolean;
      lastTick?: number;
    }
  | { op: ClientOp.Start }
  | { op: ClientOp.Sync; requestId: number; lastTick: number }
  | { op: ClientOp.Ack; roomId: string; tick: number }
  /** The town's presence report (see ClientOp.HubMove): no seq — the hub has
   *  no sim, so there is no command stream for it to enter. */
  | { op: ClientOp.HubMove; x: number; y: number }
  /** The dungeon door (see ClientOp.OpenDoor): the host's choice of floor
   *  type + size, and nothing else — the seed is the server's, at the
   *  instant of opening. */
  | { op: ClientOp.OpenDoor; floor: number; size: "small" | "medium" | "large" }
  /** The party verbs (see the ClientOp entries): hub member ids, no seq. */
  | { op: ClientOp.PartyInvite; target: number }
  | { op: ClientOp.PartyAccept; from: number }
  | { op: ClientOp.PartyDecline; from: number }
  | { op: ClientOp.PartyLeave }
  | { op: ClientOp.PartyTransfer; target: number }
  /** Found a party of one (see ClientOp.PartyCreate): no fields, no seq. */
  | { op: ClientOp.PartyCreate }
  /** The race verbs (see ClientOp.Race*): hub member ids / wire race ids, no
   *  seq — the hub has no sim for them to enter. */
  | { op: ClientOp.RaceChallenge; target: number; floor: number; size: "medium" | "large"; rated: boolean }
  | { op: ClientOp.RaceAccept; race: number }
  | { op: ClientOp.RaceReject; race: number }
  | { op: ClientOp.RaceBegin; race: number }
  /** A player card request (see ClientOp.InspectPlayer): the target's HUB member
   *  id, which is the only handle the town's menus hold. */
  | { op: ClientOp.InspectPlayer; id: number }
  /** Drop a pocket item at your feet (see ClientOp.DropItem): the pocket
   *  index, like UseItem. */
  | { op: ClientOp.DropItem; seq: number; slot: number }
  /** The town's chat (see ClientOp.HubChat): no seq, hub-only. */
  | { op: ClientOp.HubChat; message: string }
  /** replace the saved class/gear through the hub service NPC. */
  | ClientPacketWithPlaytest;

export interface PlaytestLoadoutWire {
  classKind: HeroClass;
  level: number;
  equipment: Record<EquipSlot, number | null>;
}

/** Hub-only data for a draft build preview, using the game's shared composition. */
export interface PlaytestCatalogWire {
  items: readonly ItemDef[];
  classes: ClassCatalog;
  combat: CombatTables;
}

export interface HubNpcWire {
  id: "bull";
  name: "The Bull";
  x: number;
  y: number;
  dir: "n" | "s" | "e" | "w";
  moving: boolean;
}

// Extend the discriminated union after the shared wire shapes are declared.
// This keeps the packet definition close to the decoder while allowing the
// loadout shape to be reused by Welcome/ack packets.
export type ClientPacketWithPlaytest = { op: ClientOp.PlaytestLoadout; classKind: HeroClass; level: number; equipment: Record<EquipSlot, number | null> };

function isInt(v: unknown): v is number {
  return typeof v === "number" && Number.isInteger(v);
}

function isIntArray(v: unknown): v is number[] {
  return Array.isArray(v) && v.every(isInt);
}

function isKind(v: unknown): v is InteractKind {
  return (
    v === "door" || v === "boss" || v === "key" || v === "stone" ||
    v === "portal" || v === "item" || v === "sack" || v === "puzzle-imbue" || v === "puzzle-force" || v === "inspect-chasm" || v === "wizard-orb"
  );
}

function isPocketItems(v: unknown): v is PocketItem[] {
  return (
    Array.isArray(v) &&
    v.every(
      (r) =>
        typeof r === "object" && r !== null &&
        (r.cls === "equipment" || r.cls === "consumable") &&
        typeof r.kind === "number" && Number.isInteger(r.kind),
    )
  );
}

function isWhich(v: unknown): v is "personal" | "group" {
  return v === "personal" || v === "group";
}

function isItemKind(v: unknown): v is ItemKind {
  return v === "key" || v === "stone";
}

function isPlaytestEquipment(v: unknown): v is Record<EquipSlot, number | null> {
  if (!v || typeof v !== "object" || Array.isArray(v)) return false;
  const r = v as Record<string, unknown>;
  if (Object.keys(r).length !== EQUIP_SLOTS.length) return false;
  return EQUIP_SLOTS.every((slot) => {
    const value = r[slot];
    return value === null || (isInt(value) && value >= 0);
  });
}

export function encodeClientPacket(p: ClientPacket): string {
  return JSON.stringify(p);
}

/**
 * Client packets are untrusted. Malformed input returns null — never throws
 * across the boundary. seq is the client's monotonic sequence number; the
 * server stamps the tick on receipt (the client never asserts what tick it is
 * on — that is a trivial cheat). Hello/Start are pre-game and carry no seq.
 */
export function decodeClientPacket(raw: string): ClientPacket | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object") return null;
  const o = parsed as Record<string, unknown>;

  const op = o.op;
  switch (op) {
    case ClientOp.Sync:
      if (Number.isSafeInteger(o.requestId) && (o.requestId as number) >= 0 &&
          Number.isSafeInteger(o.lastTick) && (o.lastTick as number) >= -1)
        return { op, requestId: o.requestId as number, lastTick: o.lastTick as number };
      return null;
    case ClientOp.Ack:
      if (typeof o.roomId === "string" && o.roomId.length <= 128 &&
          Number.isSafeInteger(o.tick) && (o.tick as number) >= -1)
        return { op, roomId: o.roomId, tick: o.tick as number };
      return null;
    case ClientOp.Move:
      if (isInt(o.seq) && isInt(o.x) && isInt(o.y))
        return { op, seq: o.seq, x: o.x, y: o.y };
      return null;
    case ClientOp.Interact:
      if (isInt(o.seq) && isInt(o.targetId) && isKind(o.kind))
        return { op, seq: o.seq, kind: o.kind, targetId: o.targetId };
      return null;
    case ClientOp.UseAbility:
      if (isInt(o.seq) && isInt(o.abilityId) && isInt(o.targetId)) {
        // The aimed tile rides as a pair (both or neither — a half tile is
        // malformed, like any other).
        const x = isInt(o.x) ? o.x : null;
        const y = isInt(o.y) ? o.y : null;
        if ((x === null) !== (y === null)) return null; // a half tile is malformed
        // The telegrab item reference rides as its own pair: an unknown kind
        // is malformed (the sim validates the id against state).
        const kind = o.targetKind === undefined ? undefined : isItemKind(o.targetKind) ? o.targetKind : null;
        if (kind === null) return null;
        return {
          op,
          seq: o.seq,
          abilityId: o.abilityId,
          targetId: o.targetId,
          ...(x === null || y === null ? {} : { x, y }),
          ...(kind === undefined ? {} : { targetKind: kind }),
        };
      }
      return null;
    case ClientOp.PlaceGatestone:
      if (isInt(o.seq) && isInt(o.stoneId))
        return { op, seq: o.seq, stoneId: o.stoneId };
      return null;
    case ClientOp.TeleportGatestone:
      if (isInt(o.seq) && isWhich(o.which))
        return { op, seq: o.seq, which: o.which };
      return null;
    case ClientOp.Chat:
      if (isInt(o.seq) && typeof o.message === "string")
        return { op, seq: o.seq, message: o.message };
      return null;
    case ClientOp.GenerateGatestone:
    case ClientOp.LeaveFloor:
      if (isInt(o.seq)) return { op, seq: o.seq };
      return null;
    case ClientOp.ReorderInventory:
      // All three arrays: the sim validates each is an exact permutation of
      // what the player holds (keys, carried stones, unequipped items).
      if (
        isInt(o.seq) &&
        isIntArray(o.keys) &&
        isIntArray(o.carried) &&
        isPocketItems(o.items) &&
        o.keys.length <= 64 &&
        o.carried.length <= 64 &&
        o.items.length <= 64
      )
        return { op, seq: o.seq, keys: o.keys, carried: o.carried, items: o.items };
      return null;
    case ClientOp.DropKey:
      if (isInt(o.seq) && isInt(o.keyId))
        return { op, seq: o.seq, keyId: o.keyId };
      return null;
    case ClientOp.DropItem:
      // Loose here, strict in the sim: the slot must be a live pocket entry
      // (the sim resolves the (class, kind) pair — a stale index after a
      // reorder/pickup is a game-state rejection, not a malformed packet,
      // the same rule as UseItem).
      if (isInt(o.seq) && isInt(o.slot) && o.slot >= 0 && o.slot < 64)
        return { op, seq: o.seq, slot: o.slot };
      return null;
    case ClientOp.EquipItem:
      // Loose here, strict in the sim: the kind index must exist in the
      // shared catalog (shared/items.ts) or the sim fires an invalid event.
      if (isInt(o.seq) && isInt(o.itemId) && o.itemId >= 0 && o.itemId < 64)
        return { op, seq: o.seq, itemId: o.itemId };
      return null;
    case ClientOp.UnequipItem:
      // Loose here, strict in the sim: the slot must be one of EQUIP_SLOTS
      // (shared/items.ts) or the sim fires an invalid event.
      if (isInt(o.seq) && typeof o.slot === "string" && o.slot.length <= 16)
        return { op, seq: o.seq, slot: o.slot };
      return null;
    case ClientOp.Attack:
      if (
        isInt(o.seq) &&
        isInt(o.targetId) &&
        o.targetId >= 0 &&
        typeof o.targetIsEnemy === "boolean"
      )
        return { op, seq: o.seq, targetId: o.targetId, targetIsEnemy: o.targetIsEnemy };
      return null;
    case ClientOp.UseItem:
      // Loose here, strict in the sim: the slot must be a live pocket entry
      // that is a CONSUMABLE, or the sim fires an invalid event (a stale
      // index after a reorder/pickup is a game-state rejection, not a
      // malformed packet).
      if (isInt(o.seq) && isInt(o.slot) && o.slot >= 0 && o.slot < 64)
        return { op, seq: o.seq, slot: o.slot };
      return null;
    case ClientOp.HubMove:
      // Type-checked here, bounded there: a tile outside the plane is a
      // cosmetic oddity the hub clamps, not a malformed packet (§5 reserves
      // dropping the connection for input that is not a packet at all).
      if (isInt(o.x) && isInt(o.y)) return { op, x: o.x, y: o.y };
      return null;
    case ClientOp.OpenDoor:
      // Loose here, strict in the server: the id must exist in FLOORS or the
      // server closes the connection (a stale client/build). No seed field
      // exists to decode — the server picks it (hub.md's seed rule).
      if (
        isInt(o.floor) &&
        o.floor >= 0 &&
        (o.size === "small" || o.size === "medium" || o.size === "large")
      )
        return { op, floor: o.floor, size: o.size };
      return null;
    // The party verbs (hub.md): hub member ids, loose here — the server
    // resolves them against the roster (an unknown id is a refusal, not a
    // malformed packet; the ids are server-assigned, so a wrong one is a
    // stale client, not a lie).
    case ClientOp.PartyInvite:
      if (isInt(o.target)) return { op, target: o.target };
      return null;
    case ClientOp.PartyAccept:
    case ClientOp.PartyDecline:
      if (isInt(o.from)) return { op, from: o.from };
      return null;
    case ClientOp.PartyLeave:
      return { op };
    case ClientOp.PartyTransfer:
      if (isInt(o.target)) return { op, target: o.target };
      return null;
    case ClientOp.PartyCreate:
      return { op };
    // The race verbs (keyracing.md C1): loose here, strict in the server — the
    // ids are server-assigned, so a wrong one is a stale client rather than a
    // lie, and the floor id must resolve against FLOORS there. `size` is
    // narrowed to the RACEABLE pair here: small is not a race, and a client
    // that offers one is not sending a packet anyone chose to ship (§5).
    case ClientOp.RaceChallenge:
      if (
        isInt(o.target) &&
        isInt(o.floor) &&
        o.floor >= 0 &&
        (o.size === "medium" || o.size === "large") &&
        typeof o.rated === "boolean"
      )
        return { op, target: o.target, floor: o.floor, size: o.size, rated: o.rated };
      return null;
    case ClientOp.RaceAccept:
    case ClientOp.RaceReject:
    case ClientOp.RaceBegin:
      if (isInt(o.race) && o.race >= 0) return { op, race: o.race };
      return null;
    // A hub member id (server-assigned, so a wrong one is a stale client rather
    // than a lie). Bounded like every other id: an unbounded int is malformed.
    case ClientOp.InspectPlayer:
      if (isInt(o.id) && o.id >= 0 && o.id <= 1_000_000) return { op, id: o.id };
      return null;
    case ClientOp.HubChat:
      // Bounded string: an empty message is not a chat line, and a run-on
      // past the knob's max (1000) is malformed — the server would truncate
      // it anyway, so refuse a string nobody chose to ship.
      if (
        typeof o.message === "string" &&
        o.message.length > 0 &&
        o.message.length <= 1000
      )
        return { op, message: o.message };
      return null;
    case ClientOp.PlaytestLoadout:
      if (
        isHeroClass(o.classKind) &&
        isInt(o.level) &&
        o.level >= 1 &&
        o.level <= 5 &&
        isPlaytestEquipment(o.equipment)
      ) {
        return { op, classKind: o.classKind, level: o.level, equipment: o.equipment };
      }
      return null;
    case ClientOp.Hello: {
      if (typeof o.name !== "string") return null;
      const out: ClientPacket = { op, name: o.name };
      if (o.roomId !== undefined) {
        if (typeof o.roomId !== "string") return null;
        out.roomId = o.roomId;
      }
      if (o.token !== undefined) {
        if (typeof o.token !== "string") return null;
        out.token = o.token;
      }
      if (o.size !== undefined) {
        if (o.size !== "small" && o.size !== "medium" && o.size !== "large") return null;
        out.size = o.size;
      }
      if (o.capacity !== undefined) {
        if (!isInt(o.capacity)) return null;
        out.capacity = o.capacity;
      }
      if (o.seed !== undefined) {
        if (!isInt(o.seed)) return null;
        out.seed = o.seed;
      }
      if (o.floor !== undefined) {
        // Loose here, strict in the server: the id must exist in FLOORS
        // (shared/floors.ts) or the server closes the connection.
        if (!isInt(o.floor) || o.floor < 0) return null;
        out.floor = o.floor;
      }
      if (o.auth !== undefined) {
        // Opaque session token — the server resolves it; the protocol only
        // checks it is a bounded string (never logged, never echoed back).
        if (typeof o.auth !== "string" || o.auth.length === 0 || o.auth.length > 128) return null;
        out.auth = o.auth;
      }
      if (o.recovery !== undefined) {
        if (typeof o.recovery !== "boolean") return null;
        out.recovery = o.recovery;
      }
      if (o.lastTick !== undefined) {
        if (!Number.isSafeInteger(o.lastTick) || (o.lastTick as number) < -1) return null;
        out.lastTick = o.lastTick as number;
      }
      return out;
    }
    case ClientOp.Start:
      return { op };
    default:
      return null;
  }
}

/**
 * Server → client packets are trusted (we produced them) — encode only, no
 * validation on this path (§5 AGENTS.md). `observation` and `events` on Tick
 * are sim-shaped JSON payloads (see shared/observation.ts and sim types).
 */
export enum ServerOp {
  Welcome = 1,
  RoomState = 2,
  Tick = 3,
  Error = 4,
  /**
   * The hub's roster (docs/design/hub.md): the whole town, party-uniform —
   * the hub has no fog, so every member sees the same list. Sent on every
   * membership change (join/leave) after the joining member's Welcome.
   * Carries identities + looks, NEVER positions: where a player stands in
   * the town is client-side cosmetic (hub.md) and no coordinate reaches
   * the server, which is also what keeps the hub free of a sim.
   */
  HubState = 5,
  /**
   * A line of town chat (docs/design/hub.md): server-broadcast, no sim.
   * `from` is the sender's HUB member id (the roster resolves the name —
   * the packet never carries an account id). Sent to the WHOLE town: the
   * hub has no fog, so there is no per-observer filter, party or otherwise.
   */
  HubChat = 6,
  /**
   * The run's LEADERBOARD row (docs/design/leaderboard.md): the rank of the
   * floor that JUST ended, for the results screen. The sim cannot know it
   * (no accounts in sim) and the `floor-ended` event is already broadcast,
   * so the rank rides its own packet — sent per player right after the
   * floor's re-parent Welcome, while the client's results screen is still
   * up. The nearest distinct faster/slower times ride with it for context.
   * All rank fields are null on a RETREATED floor (a time you didn't
   * finish isn't a time — nothing to rank), and the packet is absent on a
   * server without accounts (arena, headless dev) or a legacy room with no
   * floor type: the results screen then simply shows no rank line.
   */
  RunRank = 7,
  /** the hub service accepted a class/gear replacement. */
  PlaytestLoadout = 8,
  /** Current observation; requestId is the resync barrier, absent on resume. */
  Snapshot = 9,
  /** Read-only results for a floor that ended during a disconnect. */
  FinishedFloor = 10,
  /**
   * The town's OPEN challenges and ARMED races (docs/design/keyracing.md C1).
   * Party-uniform like `HubState.parties`/`invites` — the hub has no fog, so
   * every member of a named party sees the same challenge interface, which is
   * what makes unanimity checkable by the players themselves.
   *
   * Carries both rosters and EACH MEMBER'S ACCEPT STATE (there is no countdown
   * to render: an offer either has everyone or it does not), the locked terms,
   * the category and the remaining window. **It never carries the seed** — the
   * seed is drawn at arm and stays server-side, because a race floor's map is
   * the thing worth protecting (a scouted map is a scouted map).
   */
  RaceState = 11,
  /**
   * A side's race ENDED without a valid clear (docs/design/keyracing.md C3).
   * `ServerOp.RaceState` is the STATE (both sides' outcomes ride it once they
   * exist); this is the EVENT that explains one, sent to the members of the
   * side it happened to — who may be landing back in town at this very moment,
   * which is why it is not simply another town broadcast.
   *
   * **Server→client, and deliberately not the other way round.** Every forfeit
   * condition is something the server watched happen (a floor that resolved
   * with the boss alive, a door that never opened); a client-asserted forfeit
   * would be a claim about the OTHER side, which is exactly the assertion the
   * `seq`-not-`tick` rule refuses (§5). `reason` is display: to the rating math
   * a forfeit, a DNF and a loss are all `s = 0`.
   */
  RaceForfeit = 12,
  /**
   * One player's card, answering `ClientOp.InspectPlayer` (docs/design/keyracing.md
   * D1b): look, saved build, fastest clears, rating. Sent to the ONE socket that
   * asked — it is a reply, not another town broadcast, because the answer changes
   * nothing about what the town looks like until someone asks what it means.
   *
   * Nothing here is hidden information: the boards already publish a party's
   * names, classes and times, and `/api/ratings` is unauthenticated. The one
   * genuinely new disclosure is the build (the record book does not publish gear),
   * and it is the account's SAVED six slots rather than live floor equipment —
   * which is what a profile means, cannot go stale mid-floor, and keeps the sim
   * unread by any menu.
   */
  PlayerCard = 13,
}

export interface PartyMemberInfo {
  id: number;
  name: string;
  connected: boolean;
  /**
   * The account's saved look (shared/appearance.ts) — absent for a player
   * without an account (headless, arena, old clients), who keeps the
   * historical per-player identity tint. Server-produced and trusted;
   * the client re-validates before rendering (defensive only) and the
   * sim never sees it.
   */
  appearance?: HeroAppearance;
}

/**
 * A town party on the wire (docs/design/hub.md, amended by
 * docs/design/keyracing.md A1): the members' HUB member ids and the host's.
 * Every party with ≥1 member currently in the town is listed — a party of
 * one is a real party (it is what holds a solo race, and it is what the door
 * requires), and a party that walked through the door is a floor thing until
 * it comes back. The host is the party's
 * STORED leader (amended 2026-10-25): the inviter of the accept that
 * founded it, transferable by the current host (`PartyTransfer`); when a
 * host leaves or drops the server re-derives it to the lowest account id
 * still in (run.md's rule, now the fallback).
 */
export interface PartyWire {
  host: number;
  members: number[];
}

/**
 * A pending group-up invite on the hub's wire: HUB member ids, party-
 * uniform like the roster (the town has no fog — everyone sees the
 * invites, the invitee acts on them). An invite dies with its inviter's or
 * invitee's socket, and with the invitee joining any party; the next
 * broadcast drops it.
 */
export interface InviteWire {
  from: number;
  to: number;
}

/**
 * One member of a race side on the wire (docs/design/keyracing.md C1): the
 * HUB member id, the name, and whether they have accepted. `accepted` is what
 * replaces the old countdown — with no timer the interface has to show WHO is
 * holding the race up, because that player is the reason it has not armed.
 * The issuing host is accepted from the moment of issue: their issue IS their
 * acceptance.
 *
 * The optional block after `accepted` is the STAKE preview (D2, 11-15): what
 * THIS player stands to win or lose, computed by the same `previewRace` math
 * that lands the verdict, so the window cannot promise a number the resolution
 * fails to produce. It is present only while the offer is OPEN and RATED —
 * after arming the rosters are locked and the stakes are no longer a reason to
 * be looking — and absent for an unrated race, where there is nothing at stake
 * to show (the window drops the columns rather than drawing a column of dashes).
 * `elo`/`rd`/`races` are on the display scale the ladder itself uses (Elo and
 * RD as a player reads them); `races === 0` is a provisional player, which is
 * why the swings beside it are enormous. Absent fields mean "no preview", and
 * a client must draw no numbers, not zeros.
 */
export interface RaceMemberWire {
  id: number;
  name: string;
  accepted: boolean;
  /** Display Elo on this race's ladder (the provisional start reads 1500). */
  elo?: number;
  /** Display rating deviation — the reason a big stake is big. */
  rd?: number;
  /** Rated races on this ladder. 0 = provisional. */
  races?: number;
  /** Integer Elo at stake, in each direction. Win/loss only; a tie is its own
   *  number because the window shows the tie case honestly (½–½). */
  win?: number;
  loss?: number;
  tie?: number;
}

/** How a side's race ended WITHOUT a valid clear (keyracing.md §Win, lose,
 *  forfeit). The words are display-only — the rating scores every one of them
 *  as a loss — but a player told they forfeited with no reason reads it as a
 *  bug, so the distinction the design bothers to name is the distinction the
 *  wire carries.
 *
 *  - `retreated` — the floor resolved with the boss alive (by the exit portal,
 *    or by every player walking back through the return portal: leaving is how
 *    a floor ENDS, it is not itself a forfeit);
 *  - `incomplete` — the boss died but the map is below 100%;
 *  - `abandoned` — nobody was there when it resolved (every socket died and the
 *    abandonment grace ended the floor);
 *  - `no_show` — the side's host never opened the race door inside the 30-minute
 *    start window. */
export type RaceForfeitReason = "retreated" | "incomplete" | "abandoned" | "no_show";

/** A side's result once it has one (keyracing.md C3): `clear` is boss dead AND
 *  100% mapped, `forfeit` is every other way a side's race can end. */
export type RaceSideResult = "clear" | "forfeit";

/** One side of a race on the wire: the party's host (hub id) and its members.
 *  A side is a PARTY, never a person (keyracing.md) — including a party of one,
 *  which is what the solo ladder is. */
export interface RaceSideWire {
  host: number;
  members: RaceMemberWire[];
  /**
   * The side's AGGREGATE rating on this race's ladder (mean Elo of its members
   * — the single opponent each player is actually scored against, which is what
   * makes a duo racing a trio sane without a handicap). Display, and null when
   * there is no rated preview to read it off.
   */
  agg: number | null;
  /**
   * This side is IN its floor: its host opened the race door (C2). Two sides
   * open independently and at different moments, so "the other side has not
   * started yet" is a state the town has to be able to show — it is the one
   * thing a waiting side is waiting on.
   */
  started: boolean;
  /**
   * This side's outcome, or null while it is still owed one (keyracing.md C3).
   * A side resolves independently: one side can be home with a time while the
   * other is still in its dungeon, and the town has to be able to show that
   * rather than a race that looks stalled.
   */
  outcome: RaceSideResult | null;
  /**
   * The side's clear time in ticks, or null if it never got one. A FORFEIT's
   * time rides along as display ("cleared 80% in 9:12") and never competes —
   * only a valid clear's time decides a winner.
   */
  ticks: number | null;
}

/**
 * A race the town can see (docs/design/keyracing.md C1). `id` is the registry's
 * small integer, unique within this array (traps/id-collision.md — ids are
 * unique *within* an array, and the client sends it back to name which race it
 * is answering, so a stale window must be re-read from the freshest packet).
 *
 * `state` is the whole lifecycle a client can act on: `open` waits on
 * acceptances and can be rejected by either host or simply expire; `armed` has
 * locked its seed and rosters and waits for a host to open the race door (C2);
 * `racing` means BOTH sides are in their floors, which is the point after which
 * no race clock exists at all. A revoked or expired challenge is not on the
 * wire at all — it is REMOVED, so the client's window closing is the same event
 * as the offer dying.
 *
 * `expiresInMs` is a REMAINING duration measured at broadcast, never a
 * timestamp: the client's clock is not the server's and the town has no reason
 * to learn what time it is. It is the CHALLENGE window and nothing else — 0
 * once armed, because what that window bounds is ANSWERING.
 *
 * `startsInMs` is the same shape for the 30-minute START window (D2, 11-15):
 * the party panel's countdown, so a host can see how long they have to open the
 * door. It is DISPLAY ONLY and it changes who enforces nothing — the server
 * forfeits the side that stayed home whether or not any client is watching, and
 * no client may be trusted to be the one that notices it. ONE value for the
 * race, not one per side, because `race.startDeadline` is one: it counts down
 * while at least one side has yet to walk in, and it is 0 once both are in,
 * because from `racing` there is no race clock at all.
 *
 * **No seed field exists.** C1's audit greps every packet for it.
 */
export interface RaceWire {
  id: number;
  state: "open" | "armed" | "racing";
  /** The issuing host's HUB member id (who to thank, or blame). */
  from: number;
  /** Terms, locked at issue: the floor recipe id (floors.store) and a size
   *  that is always medium or large — small is not raceable. */
  floor: number;
  size: "medium" | "large";
  /** Rated races move Glicko-2; unrated ones play out and change nothing. */
  rated: boolean;
  /** The group-size ladder this race scores on: max(side sizes). */
  category: number;
  expiresInMs: number;
  /** Remaining START window in ms (see the header): 0 unless a side still has
   *  to open its door. Display only — the forfeit is the server's. */
  startsInMs: number;
  sides: [RaceSideWire, RaceSideWire];
}

/**
 * A town presence on the wire: identity + look + where they REPORT standing
 * (`ClientOp.HubMove`). `x`/`y` are on every hub packet — a presence the
 * server has not heard from yet stands at the plane's centre until their
 * first report — and on NO floor packet by construction: floor rooms build
 * their party list from their own players, so town coordinates can never
 * leak onto a run's wire.
 */
export interface HubMemberInfo extends PartyMemberInfo {
  x: number;
  y: number;
}

/**
 * One ladder on a player's card (keyracing.md D1b), on the DISPLAY scale the
 * ladder itself uses (Elo and RD as a player reads them) — straight out of the
 * `rating` table, so the card and the hiscore can never disagree. `races` is
 * always ≥ 1 here: a row exists only after a RATED race, which is why an
 * account that has never raced has no `shown` ladder rather than a fake one.
 */
export interface RatingLadderWire {
  /** The group-size ladder, 1..5. `partyLabel()` renders the word. */
  category: number;
  elo: number;
  rd: number;
  races: number;
  /** Few enough rated races that the number has not settled (the hiscore's own
   *  threshold, so a card and a ladder cannot call the same player settled and
   *  provisional at once). */
  provisional: boolean;
}

/**
 * One fastest clear on a player's card. Race floors are excluded by the board's
 * own filter (`accounts/index.ts` `qualified`): a race clear is a real result on
 * the ladder and not a record on the boards, and a profile that disagreed with
 * the record book about which is which would be a bug nobody could explain.
 */
export interface PlayerBestWire {
  floor: number;
  floorName: string;
  size: "small" | "medium" | "large";
  partySize: number;
  ticks: number;
  endedAt: number;
}

/**
 * A player's card (docs/design/keyracing.md D1b): what a town player looks like
 * from the outside — their look, the build they would walk into a floor with,
 * their fastest clears, and their rating.
 *
 * **Every field is optional and absence is a real answer, never a zero.** A
 * presence with no account (headless, the arena, a client predating accounts)
 * has no look, no saved build, no runs and no rating. A card with no `ladders`
 * reads as the provisional start, which is what the rating model says an unraced
 * account IS: there is no "unrated" state to display, and the word *unrated*
 * belongs to a race term (`RaceWire.rated`), not to a person.
 *
 * `shown` is the ladder the card FEATURES, resolved SERVER-side so every client
 * agrees on one answer: the account's own override if it set one, else its
 * highest rating with ties going to the LARGER group size. Null means no ladder
 * is above the provisional start, so the plate draws 1500 with no category word
 * (the tiebreak would otherwise hand a never-raced account "5 man" — the ladder
 * they have played least, which is nothing). An override onto a ladder the
 * account has never raced is answered with the provisional start ON THAT LADDER
 * (`races: 0`), which is the only way `races` can be 0 on this wire: the choice
 * names a ladder, and an empty ladder is still the ladder they asked to be read
 * by.
 *
 * `loadout` is the account's SAVED six slots, not live floor gear: a profile
 * says what someone goes in with, it cannot go stale mid-floor, and the sim is
 * never read to decorate a menu.
 */
export interface PlayerCardWire {
  /** The hub member id this answers. Ids are unique WITHIN the roster array
   *  (traps/id-collision.md), so a card that lands after a reconnect must be
   *  matched to the presence it names, not assumed to be the open menu's. */
  id: number;
  name: string;
  cls?: HeroClass;
  appearance?: HeroAppearance;
  equipment?: Record<EquipSlot, number | null>;
  bests?: PlayerBestWire[];
  shown?: RatingLadderWire | null;
  ladders?: RatingLadderWire[];
  /** `shown` came from the account's own override, not the auto rule — the
   *  difference a future "(their choice)" note would read off. */
  override?: boolean;
}

export type ServerPacket =
  | {
      op: ServerOp.Welcome;
      playerId: number;
      roomId: string;
      /**
       * Reconnect credential for resuming this player slot. The hub omits
       * it: a hub member who drops leaves the town (no frozen slot), and
       * the client's reconnect fallback re-joins with a fresh Hello.
       */
      token?: string;
      /** Next command sequence accepted by this floor slot. */
      nextSeq?: number;
      /** The room creator (a floor's party lead) — -1 for the hub (no
       *  host there yet; the door's host is the party's). */
      hostId: number;
      /** Floor rooms only — the hub is a fixed town with no floor size. */
      size?: string;
      /** Floor rooms only — the hub's capacity is a server knob, not part
       *  of the space's identity. */
      capacity?: number;
      /**
       * The room's party (a floor) — or the whole HUB roster (the town):
       * for a hub Welcome (roomId = HUB_ROOM_ID) this is everyone in it.
       */
      party: PartyMemberInfo[];
      /**
       * The room's floor recipe id (floors.store — shared/floors.ts); the
       * lobby resolves the name from FLOORS. Absent = a legacy all-NPC room
       * (created without a floor type — the headless paths).
       */
      floor?: number;
      /**
       * GODMODE ONLY (npm run dev -- --godmode): the floor seed, so a human
       * can paste it for bug reports. Never sent by a normal server — this is
       * the deliberate, dev-gated exception to "the seed never leaves the
       * server" (AGENTS §6). Clients render it only when present.
       */
      seed?: number;
      /**
       * The hub's party structure (a hub Welcome ONLY — roomId =
       * HUB_ROOM_ID): the town's parties + pending invites. Absent on a
       * floor's Welcome: a floor's party list is just its players, and
       * nothing in sim/ knows parties exist (AGENTS §15).
       */
      parties?: PartyWire[];
      invites?: InviteWire[];
      /**
       * The hub's open challenges + armed races (a hub Welcome ONLY —
       * keyracing.md C1): a player who walks into the town late must see the
       * challenge the town is already holding up, or unanimity reads as a bug
       * to the last person to arrive. Absent on a floor's Welcome — a floor
       * knows nothing about races (nothing in sim/ does).
       */
      races?: RaceWire[];
      /** Hub-only deterministic service NPC presentation. */
      npcs?: HubNpcWire[];
      /** Hub-only Bull service capability and the account's current build. */
      playtestLoadout?: PlaytestLoadoutWire;
      playtestCatalog?: PlaytestCatalogWire;
    }
  | {
      op: ServerOp.RoomState;
      status: "waiting" | "running" | "complete";
      players: PartyMemberInfo[];
      hostId: number;
    }
  | { op: ServerOp.Tick; tick: number; observation: unknown; events: unknown[]; backlogTicks?: number }
  | { op: ServerOp.Snapshot; roomId: string; tick: number; nextSeq: number; requestId?: number; observation: unknown; events: unknown[]; backlogTicks: number }
  | {
      op: ServerOp.FinishedFloor;
      info: { roomId: string; playerId: number; hostId: number; party: PartyMemberInfo[]; size: string; capacity: number; floor?: number };
      tick: number;
      observation: unknown;
      event: unknown;
      rank?: Extract<ServerPacket, { op: ServerOp.RunRank }>;
    }
  | { op: ServerOp.Error; reason: string }
  /** The town's roster + its party structure (parties with ≥2 members in
   *  the town, pending invites — all party-uniform: the hub has no fog). */
  | {
      op: ServerOp.HubState;
      players: HubMemberInfo[];
      parties: PartyWire[];
      invites: InviteWire[];
      npcs?: HubNpcWire[];
    }
  /** A line of town chat (see ServerOp.HubChat): the sender's hub id + text. */
  | { op: ServerOp.HubChat; from: number; message: string }
  /** The town's open challenges + armed races (see RaceWire). Town-wide, like
   *  every hub packet: the town has no fog, and a challenge nobody can see is
   *  a challenge nobody can accept. */
  | { op: ServerOp.RaceState; races: RaceWire[] }
  /** One side forfeited (see ServerOp.RaceForfeit): the registry's wire id, the
   *  side, and why. No seed, no roster, no time — the state packet carries those
   *  and this packet's whole job is the reason. */
  | { op: ServerOp.RaceForfeit; race: number; side: 0 | 1; reason: RaceForfeitReason }
  /** One player's card (see ServerOp.PlayerCard), to the socket that asked. */
  | { op: ServerOp.PlayerCard; card: PlayerCardWire }
  /** Rank and the nearest distinct faster/slower clear times (ticks) in the
   * same floor-type × dungeon-size × finishing-party-size bucket (the client
   * already knows its own size from the Welcome — no wire field needed);
   * null ranks on DNF/DQ; runId links every persisted ended attempt. */
  | { op: ServerOp.RunRank; floor: number; rank: number | null; of: number | null; fasterTicks: number | null; fasterRank: number | null; slowerTicks: number | null; slowerRank: number | null; runId?: number }
  /** the accepted account build, echoed to the hub client. */
  | { op: ServerOp.PlaytestLoadout; loadout: PlaytestLoadoutWire };

/**
 * The `ServerOp.Error` reasons a CLIENT changes behaviour on. The wire carries
 * plain strings, so these names are the contract between the two sides — the
 * client compares them, and a renamed literal on one side fails silently as a
 * stuck client.
 *
 * `ERR_AUTH_EXPIRED` is TERMINAL: the account credential in `Hello.auth` is
 * dead (session ended, db reset, sign-out elsewhere), and the server closes
 * the socket right after saying so. Reconnecting re-sends that same dead
 * credential, so the retry can only produce the same answer — and because the
 * socket *opens* fine every time, a retry never backs off: it is a half-second
 * loop of error → auth screen rebuild, which wipes the login form under the
 * player's fingers. The client drops the credential and stops on the first one.
 *
 * `ERR_NO_RECONNECT_SLOT` ends the resume: the resume identity (roomId +
 * token) is stale — the floor ended, or the hub never freezes slots. Since
 * invalidate-on-disconnect the account session died with the dropped socket,
 * so the client's recovery is the sign-in screen (re-login), not a fresh
 * anonymous Hello.
 */
export const ERR_AUTH_EXPIRED = "auth expired";
/** A second game socket must stop without clearing the shared account token. */
export const ERR_ACCOUNT_IN_USE = "account already connected";
export const ERR_NO_RECONNECT_SLOT = "no such reconnect slot";
/** The old socket has not closed yet; retry the same floor credential. */
export const ERR_RECONNECT_BUSY = "reconnect slot still connected";

export function encodeServerPacket(p: ServerPacket): string {
  return JSON.stringify(p);
}
